import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(() => {
    try {
      const stored = localStorage.getItem("employee");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("employeeToken");
    localStorage.removeItem("employee");

    setEmployee(null);

    navigate("/login", {
      replace: true,
    });
  }, [navigate]);

  const login = (token, employeeData) => {
    localStorage.setItem("employeeToken", token);
    localStorage.setItem("employee", JSON.stringify(employeeData));

    setEmployee(employeeData);
  };

  const verifyEmployee = useCallback(async () => {
    const token = localStorage.getItem("employeeToken");

    if (!token) {
      setEmployee(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        "https://authapi.taxisafar.com/api/v1/role/me",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const employeeData = response?.data?.employee;

      // No employee data returned
      if (!response?.data?.success || !employeeData) {
        logout();
        return;
      }

      // Blocked / inactive account
      if (
        employeeData.status === "blocked" ||
        employeeData.status === "inactive" ||
        employeeData.status === false
      ) {
        logout();
        return;
      }

      // Update latest employee data
      localStorage.setItem("employee", JSON.stringify(employeeData));
      setEmployee(employeeData);
    } catch (error) {
      console.error("Employee verification failed:", error);

      const status = error?.response?.status;

      // Invalid token / deleted employee / unauthorized
      if (status === 401 || status === 403 || status === 404) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    // First hit immediately
    verifyEmployee();

    // Then verify every 30 seconds
    const interval = setInterval(() => {
      verifyEmployee();
    }, 30 * 1000);

    return () => clearInterval(interval);
  }, [verifyEmployee]);

  const isAdmin = employee?.role === "admin";

  return (
    <AuthContext.Provider
      value={{
        employee,
        login,
        logout,
        isAdmin,
        loading,
        verifyEmployee,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
